import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, Upload, Link as LinkIcon, X, Image } from "lucide-react";
import PolicyAgreement from "@/components/PolicyAgreement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPolicyAgreement, setShowPolicyAgreement] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [inspirationImages, setInspirationImages] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_category: "",
    service_type: "",
    event_date: "",
    event_time: "",
    location: "",
    guest_count: 1,
    consultation_method: "",
    inspiration_notes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/cms/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setInspirationImages(prev => [...prev, {
          url: response.data.url,
          name: file.name,
          type: 'upload'
        }]);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadingImage(false);
    e.target.value = ''; // Reset input
  };

  const handleAddImageUrl = () => {
    if (!imageUrlInput.trim()) return;
    
    // Basic URL validation
    try {
      new URL(imageUrlInput);
      setInspirationImages(prev => [...prev, {
        url: imageUrlInput,
        name: 'Image Link',
        type: 'url'
      }]);
      setImageUrlInput("");
    } catch {
      toast.error("Please enter a valid URL");
    }
  };

  const removeInspirationImage = (index) => {
    setInspirationImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.service_category || !formData.service_type || !formData.consultation_method) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Store form data and show policy agreement
    setPendingFormData({
      ...formData,
      inspiration_images: inspirationImages.map(img => img.url)
    });
    setShowPolicyAgreement(true);
  };

  const handlePolicyAgree = async (signatureData) => {
    setIsSubmitting(true);
    
    try {
      // Add signature data to form
      const submissionData = {
        ...pendingFormData,
        policy_signature: signatureData.signature,
        policy_date: signatureData.date,
        policy_agreed: true
      };

      const response = await axios.post(`${API}/inquiries`, submissionData);
      
      toast.success("Inquiry submitted successfully! Check your email for confirmation.");
      
      // Redirect to home after short delay
      setTimeout(() => {
        navigate("/");
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
      setShowPolicyAgreement(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="inquiry-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="inquiry-heading">
            Book Your Consultation
          </h1>
          <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
            Share your details and we&apos;ll schedule a 20-30 minute consultation to discuss your vision and confirm availability.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-lg shadow-sm space-y-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Contact Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="name" data-testid="name-label">Full Name *</Label>
              <Input
                id="name"
                name="name"
                data-testid="name-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" data-testid="email-label">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" data-testid="phone-label">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                data-testid="phone-input"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
              />
            </div>
          </div>

          {/* Service Selection */}
          <div className="space-y-6 pt-8 border-t">
            <h2 className="text-2xl font-bold">Service Details</h2>
            
            <div className="space-y-2">
              <Label data-testid="service-category-label">Service Category *</Label>
              <Select
                onValueChange={(value) => handleSelectChange("service_category", value)}
                value={formData.service_category}
              >
                <SelectTrigger data-testid="service-category-select" className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4">
                  <SelectValue placeholder="Choose service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bridal" data-testid="category-bridal">Bridal</SelectItem>
                  <SelectItem value="Event" data-testid="category-event">Event</SelectItem>
                  <SelectItem value="Production" data-testid="category-production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label data-testid="service-type-label">Service Type *</Label>
              <RadioGroup
                value={formData.service_type}
                onValueChange={(value) => handleSelectChange("service_type", value)}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Hair" id="hair" data-testid="service-type-hair" />
                  <Label htmlFor="hair" className="font-normal cursor-pointer">Hair</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Makeup" id="makeup" data-testid="service-type-makeup" />
                  <Label htmlFor="makeup" className="font-normal cursor-pointer">Makeup</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Both" id="both" data-testid="service-type-both" />
                  <Label htmlFor="both" className="font-normal cursor-pointer">Both</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-6 pt-8 border-t">
            <h2 className="text-2xl font-bold">Event Details</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="event_date" data-testid="event-date-label">Event Date *</Label>
                <Input
                  id="event_date"
                  name="event_date"
                  type="date"
                  data-testid="event-date-input"
                  value={formData.event_date}
                  onChange={handleInputChange}
                  required
                  className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_time" data-testid="event-time-label">Event Time *</Label>
                <Input
                  id="event_time"
                  name="event_time"
                  type="time"
                  data-testid="event-time-input"
                  value={formData.event_time}
                  onChange={handleInputChange}
                  required
                  className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" data-testid="location-label">Location *</Label>
              <Input
                id="location"
                name="location"
                data-testid="location-input"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Las Vegas, NV or destination"
                required
                className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guest_count" data-testid="guest-count-label">Number of People *</Label>
              <Input
                id="guest_count"
                name="guest_count"
                type="number"
                min="1"
                data-testid="guest-count-input"
                value={formData.guest_count}
                onChange={handleInputChange}
                required
                className="bg-transparent border-b border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-none px-0 py-4"
              />
            </div>
          </div>

          {/* Consultation Preference */}
          <div className="space-y-6 pt-8 border-t">
            <h2 className="text-2xl font-bold">Consultation Preference</h2>
            
            <div className="space-y-2">
              <Label data-testid="consultation-method-label">Preferred Consultation Method *</Label>
              <RadioGroup
                value={formData.consultation_method}
                onValueChange={(value) => handleSelectChange("consultation_method", value)}
                className="flex flex-col gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="text" id="text" data-testid="consultation-method-text" />
                  <Label htmlFor="text" className="font-normal cursor-pointer">Text Message</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="phone" id="phone" data-testid="consultation-method-phone" />
                  <Label htmlFor="phone" className="font-normal cursor-pointer">Phone Call</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="video" id="video" data-testid="consultation-method-video" />
                  <Label htmlFor="video" className="font-normal cursor-pointer">Video Call</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <Label htmlFor="inspiration_notes" data-testid="inspiration-label">Inspiration & Additional Notes (Optional)</Label>
              <Textarea
                id="inspiration_notes"
                name="inspiration_notes"
                data-testid="inspiration-input"
                value={formData.inspiration_notes}
                onChange={handleInputChange}
                placeholder="Share any inspiration, preferences, or special requests..."
                rows={4}
                className="bg-transparent border border-[#1A1A1A]/20 focus:border-[#D4AF37] rounded-lg p-4"
              />
              
              {/* Inspiration Photos Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-[#D4AF37]" />
                  Add Inspiration Photos (Optional)
                </Label>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* File Upload */}
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#D4AF37]/50 rounded-lg hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all">
                      <Upload className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-sm text-[#8C8C8C]">
                        {uploadingImage ? "Uploading..." : "Upload from Gallery"}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                  
                  {/* URL Input */}
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1 relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
                      <Input
                        type="url"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        placeholder="Paste image URL"
                        className="pl-10 border-[#1A1A1A]/20 focus:border-[#D4AF37]"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImageUrl())}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleAddImageUrl}
                      variant="outline"
                      className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Preview Uploaded Images */}
                {inspirationImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                    {inspirationImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.url.startsWith('/') ? img.url : img.url}
                          alt={`Inspiration ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-[#F2EBE5]"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/150?text=Image";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeInspirationImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-2 py-0.5 rounded">
                          {img.type === 'url' ? 'Link' : 'Upload'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-[#8C8C8C]">
                  Upload photos from your phone gallery or paste Pinterest/Instagram links for inspiration
                </p>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-8">
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-inquiry-button"
              className="w-full bg-[#D4AF37] text-white py-6 rounded-full text-lg font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Inquiry"
              )}
            </Button>
            <p className="text-center text-sm text-[#8C8C8C] mt-4">
              You&apos;ll receive a confirmation email within 24 hours to schedule your consultation.
            </p>
          </div>
        </form>
      </div>

      {/* Policy Agreement Modal */}
      {showPolicyAgreement && (
        <PolicyAgreement
          onAgree={handlePolicyAgree}
          onCancel={() => {
            setShowPolicyAgreement(false);
            setPendingFormData(null);
          }}
          clientName={formData.name}
        />
      )}
    </div>
  );
};

export default InquiryPage;